import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router";
import {
  Box, Button, Card, Chip, CircularProgress, Divider, IconButton, Input, Option, Select,
  Sheet, Stack, Switch, Textarea, Typography, FormControl, FormLabel,
} from "@mui/joy";
import { motion, AnimatePresence } from "framer-motion";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded";
import SaveRoundedIcon from "@mui/icons-material/SaveRounded";

import { useAdmin } from "../../AdminContext";
import {
  APIAdminQuizDetail, APIAdminQuizUpdate, APIAdminQuizBulkUpdate, APIAdminTraitsList,
} from "../../api_admin";

let _seq = 1;
const uid = () => `_${_seq++}`;
const num = (v) => (v === "" || v == null ? null : Number(v));

const QUESTION_TYPES = [["single", "Один вариант"], ["multiple", "Несколько"], ["scale", "Шкала"]];

function mapQuestion(q) {
  return {
    _key: uid(),
    id: q.id,
    text: q.text || "",
    description: q.description || "",
    question_type: q.question_type || "single",
    trait_id: q.trait_id || null,
    weight: q.weight ?? 1,
    is_required: q.is_required ?? true,
    is_active: q.is_active ?? true,
    order: q.order ?? 0,
    scale_min: q.scale_min ?? 0,
    scale_max: q.scale_max ?? 10,
    scale_step: q.scale_step ?? 1,
    options: (q.options || []).map((o) => ({
      _key: uid(), id: o.id, text: o.text || "",
      value: o.value ?? "", value_code: o.value_code ?? "",
      order: o.order ?? 0, is_active: o.is_active ?? true,
    })),
    _deletedOptionIds: [],
  };
}

export default function AdminQuizEditorPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { adminToken, canEdit } = useAdmin();

  const [loading, setLoading] = useState(true);
  const [meta, setMeta] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [deletedQuestionIds, setDeletedQuestionIds] = useState([]);
  const [traits, setTraits] = useState([]);
  const [savingMeta, setSavingMeta] = useState(false);
  const [savingStruct, setSavingStruct] = useState(false);
  const [msg, setMsg] = useState(null);

  const traitById = useMemo(() => {
    const m = {};
    traits.forEach((t) => { m[t.id] = t; });
    return m;
  }, [traits]);

  const load = async () => {
    setLoading(true);
    try {
      const [detail, traitsData] = await Promise.all([
        APIAdminQuizDetail(adminToken, id),
        APIAdminTraitsList(adminToken),
      ]);
      const q = detail.quiz;
      setMeta({
        title: q.title || "", description: q.description || "", category: q.category || "",
        order: q.order ?? 0, weight: q.weight ?? 1,
        is_active: q.is_active ?? true, is_onboarding: q.is_onboarding ?? false,
        code: q.code,
      });
      setQuestions((q.questions || []).map(mapQuestion));
      setDeletedQuestionIds([]);
      setTraits(traitsData.items || []);
    } catch (e) { /* ignore */ } finally { setLoading(false); }
  };

  useEffect(() => { if (adminToken) load(); /* eslint-disable-next-line */ }, [adminToken, id]);

  const saveMeta = async () => {
    setMsg(null);
    try {
      setSavingMeta(true);
      await APIAdminQuizUpdate(adminToken, id, {
        title: meta.title, description: meta.description, category: meta.category,
        order: Number(meta.order) || 0, weight: Number(meta.weight) || 1,
        is_active: meta.is_active, is_onboarding: meta.is_onboarding,
      });
      setMsg({ type: "success", text: "Основные поля сохранены" });
    } catch (e) {
      setMsg({ type: "danger", text: e?.response?.data?.error || "Ошибка сохранения" });
    } finally { setSavingMeta(false); }
  };

  const saveStructure = async () => {
    setMsg(null);
    const payload = {
      questions: questions.map((q) => ({
        ...(q.id ? { id: q.id } : {}),
        text: q.text,
        description: q.description,
        question_type: q.question_type,
        trait_id: q.trait_id || null,
        weight: Number(q.weight) || 1,
        is_required: q.is_required,
        is_active: q.is_active,
        order: Number(q.order) || 0,
        scale_min: q.question_type === "scale" ? num(q.scale_min) : null,
        scale_max: q.question_type === "scale" ? num(q.scale_max) : null,
        scale_step: q.question_type === "scale" ? num(q.scale_step) : null,
        options: q.question_type === "scale" ? [] : q.options.map((o) => ({
          ...(o.id ? { id: o.id } : {}),
          text: o.text,
          value: num(o.value),
          value_code: o.value_code || null,
          order: Number(o.order) || 0,
          is_active: o.is_active,
        })),
        ...(q._deletedOptionIds.length ? { delete_option_ids: q._deletedOptionIds } : {}),
      })),
      ...(deletedQuestionIds.length ? { delete_question_ids: deletedQuestionIds } : {}),
    };
    try {
      setSavingStruct(true);
      const res = await APIAdminQuizBulkUpdate(adminToken, id, payload);
      if (res?.quiz) {
        setQuestions((res.quiz.questions || []).map(mapQuestion));
        setDeletedQuestionIds([]);
      } else {
        await load();
      }
      setMsg({ type: "success", text: "Структура сохранена" });
    } catch (e) {
      setMsg({ type: "danger", text: e?.response?.data?.error || "Ошибка сохранения" });
    } finally { setSavingStruct(false); }
  };

  // --- операции над вопросами/вариантами ---
  const patchQ = (key, patch) => setQuestions((prev) => prev.map((q) => (q._key === key ? { ...q, ...patch } : q)));
  const addQuestion = () =>
    setQuestions((prev) => [...prev, mapQuestion({ question_type: "single", order: prev.length, options: [] })]);
  const removeQuestion = (q) => {
    if (q.id) setDeletedQuestionIds((prev) => [...prev, q.id]);
    setQuestions((prev) => prev.filter((x) => x._key !== q._key));
  };

  const addOption = (qKey) =>
    setQuestions((prev) => prev.map((q) => q._key === qKey
      ? { ...q, options: [...q.options, { _key: uid(), text: "", value: "", value_code: "", order: q.options.length, is_active: true }] }
      : q));
  const patchOpt = (qKey, oKey, patch) =>
    setQuestions((prev) => prev.map((q) => q._key === qKey
      ? { ...q, options: q.options.map((o) => (o._key === oKey ? { ...o, ...patch } : o)) }
      : q));
  const removeOpt = (qKey, opt) =>
    setQuestions((prev) => prev.map((q) => {
      if (q._key !== qKey) return q;
      const delIds = opt.id ? [...q._deletedOptionIds, opt.id] : q._deletedOptionIds;
      return { ...q, options: q.options.filter((o) => o._key !== opt._key), _deletedOptionIds: delIds };
    }));

  if (loading || !meta) {
    return <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}><CircularProgress /></Box>;
  }

  const readOnly = !canEdit;

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <IconButton variant="soft" color="neutral" onClick={() => navigate("/admin")}><ArrowBackRoundedIcon /></IconButton>
          <Box>
            <Typography level="h3">{meta.title || "Тест"}</Typography>
            <Typography level="body-xs" sx={{ fontFamily: "monospace", color: "text.tertiary" }}>{meta.code}</Typography>
          </Box>
        </Stack>
      </Stack>

      {msg && (
        <Sheet variant="soft" color={msg.type} sx={{ borderRadius: "md", p: 1, mb: 2 }}>
          <Typography level="body-sm">{msg.text}</Typography>
        </Sheet>
      )}

      {/* Основные поля */}
      <Card sx={{ borderRadius: "xl", mb: 3 }}>
        <Typography level="title-md">Основное</Typography>
        <Stack spacing={1.5}>
          <FormControl>
            <FormLabel>Название</FormLabel>
            <Input value={meta.title} onChange={(e) => setMeta({ ...meta, title: e.target.value })} disabled={readOnly} />
          </FormControl>
          <FormControl>
            <FormLabel>Описание</FormLabel>
            <Textarea minRows={2} value={meta.description} onChange={(e) => setMeta({ ...meta, description: e.target.value })} disabled={readOnly} />
          </FormControl>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
            <FormControl sx={{ flex: 1 }}>
              <FormLabel>Категория</FormLabel>
              <Input value={meta.category} onChange={(e) => setMeta({ ...meta, category: e.target.value })} disabled={readOnly} />
            </FormControl>
            <FormControl sx={{ width: 120 }}>
              <FormLabel>Порядок</FormLabel>
              <Input type="number" value={meta.order} onChange={(e) => setMeta({ ...meta, order: e.target.value })} disabled={readOnly} />
            </FormControl>
            <FormControl sx={{ width: 120 }}>
              <FormLabel>Вес</FormLabel>
              <Input type="number" value={meta.weight} onChange={(e) => setMeta({ ...meta, weight: e.target.value })} disabled={readOnly} />
            </FormControl>
          </Stack>
          <Stack direction="row" spacing={3}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Switch checked={meta.is_active} onChange={(e) => setMeta({ ...meta, is_active: e.target.checked })} disabled={readOnly} />
              <Typography level="body-sm">Активен</Typography>
            </Stack>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Switch checked={meta.is_onboarding} onChange={(e) => setMeta({ ...meta, is_onboarding: e.target.checked })} disabled={readOnly} />
              <Typography level="body-sm">Онбординг</Typography>
            </Stack>
          </Stack>
          {!readOnly && <Button loading={savingMeta} startDecorator={<SaveRoundedIcon />} onClick={saveMeta} sx={{ alignSelf: "flex-start" }}>Сохранить основное</Button>}
        </Stack>
      </Card>

      {/* Вопросы */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
        <Typography level="title-md">Вопросы ({questions.length})</Typography>
        {!readOnly && <Button size="sm" variant="soft" startDecorator={<AddRoundedIcon />} onClick={addQuestion}>Добавить вопрос</Button>}
      </Stack>

      <Stack spacing={2}>
        <AnimatePresence initial={false}>
          {questions.map((q, qi) => {
            const trait = q.trait_id ? traitById[q.trait_id] : null;
            const categorical = trait?.value_type === "categorical";
            return (
              <Box
                key={q._key}
                component={motion.div}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, height: 0 }}
              >
                <Card sx={{ borderRadius: "xl" }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Chip size="sm" variant="soft" color="primary">Вопрос {qi + 1}</Chip>
                    {!readOnly && (
                      <IconButton size="sm" variant="soft" color="danger" onClick={() => removeQuestion(q)}>
                        <DeleteRoundedIcon />
                      </IconButton>
                    )}
                  </Stack>

                  <FormControl>
                    <FormLabel>Текст вопроса</FormLabel>
                    <Input value={q.text} onChange={(e) => patchQ(q._key, { text: e.target.value })} disabled={readOnly} />
                  </FormControl>

                  <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                    <FormControl sx={{ flex: 1 }}>
                      <FormLabel>Тип</FormLabel>
                      <Select value={q.question_type} onChange={(_, v) => patchQ(q._key, { question_type: v })} disabled={readOnly}>
                        {QUESTION_TYPES.map(([val, lbl]) => <Option key={val} value={val}>{lbl}</Option>)}
                      </Select>
                    </FormControl>
                    <FormControl sx={{ flex: 1 }}>
                      <FormLabel>Параметр</FormLabel>
                      <Select
                        value={q.trait_id || 0}
                        onChange={(_, v) => patchQ(q._key, { trait_id: v || null })}
                        disabled={readOnly}
                      >
                        <Option value={0}>— без параметра —</Option>
                        {traits.map((t) => <Option key={t.id} value={t.id}>{t.name}</Option>)}
                      </Select>
                    </FormControl>
                    <FormControl sx={{ width: 100 }}>
                      <FormLabel>Вес</FormLabel>
                      <Input type="number" value={q.weight} onChange={(e) => patchQ(q._key, { weight: e.target.value })} disabled={readOnly} />
                    </FormControl>
                  </Stack>

                  <Stack direction="row" spacing={3}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Switch checked={q.is_required} onChange={(e) => patchQ(q._key, { is_required: e.target.checked })} disabled={readOnly} />
                      <Typography level="body-sm">Обязательный</Typography>
                    </Stack>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Switch checked={q.is_active} onChange={(e) => patchQ(q._key, { is_active: e.target.checked })} disabled={readOnly} />
                      <Typography level="body-sm">Активен</Typography>
                    </Stack>
                  </Stack>

                  {q.question_type === "scale" ? (
                    <Stack direction="row" spacing={1.5}>
                      <FormControl sx={{ flex: 1 }}>
                        <FormLabel>Мин.</FormLabel>
                        <Input type="number" value={q.scale_min} onChange={(e) => patchQ(q._key, { scale_min: e.target.value })} disabled={readOnly} />
                      </FormControl>
                      <FormControl sx={{ flex: 1 }}>
                        <FormLabel>Макс.</FormLabel>
                        <Input type="number" value={q.scale_max} onChange={(e) => patchQ(q._key, { scale_max: e.target.value })} disabled={readOnly} />
                      </FormControl>
                      <FormControl sx={{ flex: 1 }}>
                        <FormLabel>Шаг</FormLabel>
                        <Input type="number" value={q.scale_step} onChange={(e) => patchQ(q._key, { scale_step: e.target.value })} disabled={readOnly} />
                      </FormControl>
                    </Stack>
                  ) : (
                    <Box>
                      <Divider sx={{ my: 1 }} />
                      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                        <Typography level="body-sm" sx={{ fontWeight: 700 }}>
                          Варианты {categorical ? "(код категории)" : "(число для шкалы)"}
                        </Typography>
                        {!readOnly && <Button size="sm" variant="plain" startDecorator={<AddRoundedIcon />} onClick={() => addOption(q._key)}>Вариант</Button>}
                      </Stack>
                      <Stack spacing={1}>
                        {q.options.map((o) => (
                          <Stack key={o._key} direction="row" spacing={1} alignItems="center">
                            <Input
                              sx={{ flex: 1 }} placeholder="Текст варианта"
                              value={o.text} onChange={(e) => patchOpt(q._key, o._key, { text: e.target.value })} disabled={readOnly}
                            />
                            {categorical ? (
                              <Input
                                sx={{ width: 140 }} placeholder="код"
                                value={o.value_code} onChange={(e) => patchOpt(q._key, o._key, { value_code: e.target.value })} disabled={readOnly}
                              />
                            ) : (
                              <Input
                                sx={{ width: 100 }} type="number" placeholder="знач."
                                value={o.value} onChange={(e) => patchOpt(q._key, o._key, { value: e.target.value })} disabled={readOnly}
                              />
                            )}
                            {!readOnly && (
                              <IconButton size="sm" variant="plain" color="danger" onClick={() => removeOpt(q._key, o)}>
                                <DeleteRoundedIcon />
                              </IconButton>
                            )}
                          </Stack>
                        ))}
                        {q.options.length === 0 && (
                          <Typography level="body-xs" sx={{ color: "text.tertiary" }}>Нет вариантов</Typography>
                        )}
                      </Stack>
                    </Box>
                  )}
                </Card>
              </Box>
            );
          })}
        </AnimatePresence>
      </Stack>

      {!readOnly && (
        <Box sx={{ position: "sticky", bottom: 0, mt: 3, py: 2, backgroundColor: "background.body", borderTop: "1px solid", borderColor: "divider" }}>
          <Button size="lg" fullWidth loading={savingStruct} startDecorator={<SaveRoundedIcon />} onClick={saveStructure}>
            Сохранить структуру
          </Button>
        </Box>
      )}
    </Box>
  );
}